import { useRef, useEffect, useState, useCallback } from 'react'

interface RetinaCanvasResult {
  canvasRef: React.RefObject<HTMLCanvasElement>
  ctx: CanvasRenderingContext2D | null
  dpr: number
  logicalWidth: number
  logicalHeight: number
}

/**
 * Hook for handling High-DPI/Retina displays in Canvas
 * Automatically scales canvas for devicePixelRatio
 * 
 * Phase 1: Foundation & Architecture
 */
export function useRetinaCanvas(
  width: number,
  height: number
): RetinaCanvasResult {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [ctx, setCtx] = useState<CanvasRenderingContext2D | null>(null)
  const [dpr, setDpr] = useState(1)

  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    // Get current device pixel ratio
    const ratio = window.devicePixelRatio || 1
    setDpr(ratio)

    // Set actual canvas size (physical pixels)
    canvas.width = width * ratio
    canvas.height = height * ratio

    // Set display size (CSS pixels)
    canvas.style.width = `${width}px`
    canvas.style.height = `${height}px`

    // Get context and scale
    const context = canvas.getContext('2d')
    if (context) {
      context.scale(ratio, ratio)
      setCtx(context)
    }
  }, [width, height])

  // Initial setup
  useEffect(() => {
    setupCanvas()
  }, [setupCanvas])

  // Handle resize
  useEffect(() => {
    let resizeTimeout: ReturnType<typeof setTimeout>

    const handleResize = () => {
      // Debounce resize events
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(() => {
        setupCanvas()
      }, 100)
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
      clearTimeout(resizeTimeout)
    }
  }, [setupCanvas])

  // Handle zoom changes (Ctrl++, Ctrl+-)
  useEffect(() => {
    let mediaQuery: MediaQueryList | null = null
    let mediaQueryListener: ((this: MediaQueryList, ev: MediaQueryListEvent) => any) | null = null

    // Some browsers support devicePixelRatio as a media query
    if ('matchMedia' in window) {
      mediaQuery = window.matchMedia(`(resolution: ${dpr}dppx)`)
      mediaQueryListener = () => setupCanvas()
      mediaQuery.addEventListener('change', mediaQueryListener)
    }

    return () => {
      if (mediaQuery && mediaQueryListener) {
        mediaQuery.removeEventListener('change', mediaQueryListener)
      }
    }
  }, [dpr, setupCanvas])

  return {
    canvasRef,
    ctx,
    dpr,
    logicalWidth: width,
    logicalHeight: height,
  }
}
